import {
  Cushion,
  FlexContainer,
  Grid,
  MaxWidth,
  Rectangle,
  Shadow,
  Typography,
} from '@pitchero/react-ui';
import { find, flatMap, get, reject } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Collapse } from 'react-collapse';
import Icon from '../icon';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import isServer from '../../lib/is-server';
import { Trans } from '../../lib/i18n';
import FilterIconWrapper from '../navigation/mobile/filter-icon-wrapper';
import SlashNavigation from '../navigation/mobile/slash-navigation';
import SecondaryNavigationContainer from '../navigation/secondary-navigation-container';
import SecondaryNavigationLinks from '../navigation/secondary-navigation-links';
import CalendarFilters from './filters';

const CalendarFilterBar = ({
  club,
  eventType,
  teamId,
  onChangeEventType,
  onChangeTeam,
  isMobile,
  t,
}) => {
  const [openFilter, setOpenFilter] = useState(null);

  const eventTypes = ['fixtures', 'events', 'training'];
  let eventTypeFilters = eventTypes.map((cat) => ({
    label: t(`common:calendar_event_type_${cat}`),
    value: cat,
  }));

  const teamCount = club.sections.reduce((acc, section) => acc + section.teams.length, 0);

  const selectedTeamName = teamId
    ? get(
        find(
          flatMap(club.sections, (section) => section.teams),
          { id: teamId },
        ),
        'name',
      )
    : null;
  if (selectedTeamName) {
    eventTypeFilters = reject(eventTypeFilters, { value: 'events' });
  }

  const onTabClick = (filter) => {
    if (openFilter === filter && !isMobile) {
      setOpenFilter(null);
    } else {
      setOpenFilter(filter);
    }
  };

  const onChangeTeamFilterHandler = (value) => {
    setOpenFilter(null);
    onChangeTeam(value);
  };

  const onChangeEventTypeFilterHandler = (value) => {
    setOpenFilter(null);
    onChangeEventType(value);
  };

  const renderFilters = () => (
    <CalendarFilters
      eventType={eventType}
      eventTypeFilters={eventTypeFilters}
      club={club}
      isMobile={isMobile}
      onChangeTeam={onChangeTeamFilterHandler}
      onChangeEventType={onChangeEventTypeFilterHandler}
      openFilter={openFilter}
      sections={club.sections}
      teamId={teamId}
      t={t}
    />
  );

  const renderFilterControls = () => {
    if (isServer()) {
      return null;
    }

    return (
      <FlexContainer justifyContent="flex-end">
        {teamCount > 1 && (
          <FilterIconWrapper
            filterName="team"
            filterValue={selectedTeamName}
            onFilterClick={() => onTabClick('team')}
          >
            <Icon name="t-shirt" size={20} />
          </FilterIconWrapper>
        )}
        <FilterIconWrapper
          filterName="category"
          filterValue={eventType}
          onFilterClick={() => onTabClick('category')}
        >
          <Icon name="funnel" size={20} />
        </FilterIconWrapper>
      </FlexContainer>
    );
  };

  const renderFilterMenu = () => (
    <Shadow>
      <Rectangle fill="white" borderTop="thinLightGrey" component="div">
        <Collapse isOpened={!!openFilter}>
          <MaxWidth component="div" maxWidth={1366} center>
            <Cushion
              horizontal="large"
              vertical={openFilter === 'month' ? 'medium' : 'xlarge'}
              component="div"
            >
              {renderFilters()}
            </Cushion>
          </MaxWidth>
        </Collapse>
      </Rectangle>
    </Shadow>
  );

  const links = [
    {
      name: 'News',
      route: 'news',
      params: {},
      transKey: 'news:latest_news',
    },
    {
      name: 'Calendar',
      route: 'calendar',
      params: {},
      transKey: 'common:calendar',
    },
  ];

  return (
    <>
      {isMobile ? (
        <Rectangle fill="#fff">
          <Shadow shadow="dark">
            <SlashNavigation
              name="News"
              nameTransKey="common:news_and_events"
              activeSection="calendar"
              activeSectionTransKey="common:calendar"
              items={links}
            />
          </Shadow>
          <Cushion vertical="medium" horizontal="small">
            <Grid columns="3fr 2fr" alignItems="center">
              <Typography preset="staticSubtitle--large" size={24} weight="700" color="black">
                <Trans i18nKey="common:calendar">Calendar</Trans>
              </Typography>
              {renderFilterControls()}
            </Grid>
          </Cushion>
        </Rectangle>
      ) : (
        <>
          <SecondaryNavigationContainer title="News & Events" fullWidth={false}>
            <FlexContainer justifyContent="space-between" alignItems="center" style={{ flex: 1 }}>
              <Cushion left={32} component="div">
                <SecondaryNavigationLinks links={links} activeSection="Calendar" />
              </Cushion>
              {renderFilterControls()}
            </FlexContainer>
          </SecondaryNavigationContainer>
        </>
      )}
      {renderFilterMenu()}
    </>
  );
};

CalendarFilterBar.defaultProps = {
  eventType: null,
  teamId: null,
};

CalendarFilterBar.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  eventType: PropTypes.oneOf(['events', 'fixtures', 'training']),
  onChangeEventType: PropTypes.func.isRequired,
  onChangeTeam: PropTypes.func.isRequired,
  teamId: PropTypes.number,
  isMobile: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
};

export default CalendarFilterBar;
